import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LandingHero from '../components/LandingHero';
import ScoutDeskSection from '../components/landing/ScoutDeskSection';
import HowItWorksSection from '../components/landing/HowItWorksSection';
import TestimonialsSection from '../components/landing/TestimonialsSection';
import { UsageEventsTracker } from '../services/UsageEventsTracker';
import './LandingPage.css';
import './SubscriptionPlans.css';

// All images used on this page live in the public folder.

function LandingPage() {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const location = useLocation();

  // Track landing page view for analytics (anonymous sessions)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    UsageEventsTracker.recordPageView(location.pathname, document.referrer, {
      forceFlush: true,
      payload: {
        action: 'view',
        category: 'landing-page',
        entityType: 'page',
        entityId: location.pathname,
        label: document.title,
        metadata: {
          referrer: document.referrer || 'direct',
          utmSource: urlParams.get('utm_source'),
          utmMedium: urlParams.get('utm_medium'),
          utmCampaign: urlParams.get('utm_campaign'),
          utmContent: urlParams.get('utm_content'),
          utmTerm: urlParams.get('utm_term'),
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      }
    });
  }, [location.pathname, location.search]);

  // Page metadata (single consolidated effect; previously duplicated lower in file)
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    document.title = 'DocuHounds – Smarter Studying Starts Here';
    const ensureMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = content;
    };
    ensureMeta('description', 'Upload your syllabus, sync your calendar, and chat with your docs. Get started free with DocuHounds — the AI-powered academic assistant made for students.');
    ensureMeta('keywords', 'syllabus upload, calendar sync, AI study assistant, academic planner, student productivity, document chat');
  }, []);

  // Direct public paths (root-relative). No subpath base logic needed.

  // Compute runtime base so public assets resolve correctly when the app
  // is hosted under a subpath (for example '/m') or when a build-time
  // PUBLIC_URL is set. Prefer CRA's PUBLIC_URL (set at build time),
  // otherwise infer '/m' if we're inside the mobile shell.
  const runtimeBase = (process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '')
    ? process.env.PUBLIC_URL
    : (location.pathname.startsWith('/m') ? '/m' : '');
  const withBase = (path) => (runtimeBase ? `${runtimeBase}${path}` : path);

  // Screenshots for the landing carousel — use runtime public paths (place your images into public/images/screenshots)
  // Fallback generator for an inline SVG placeholder (used if an image fails to load)
  const fallbackDataURI = (label, bg = '#ffffff') => {
    const svg = `
      <svg xmlns='http://www.w3.org/2000/svg' width='1200' height='800'>
        <rect width='100%' height='100%' fill='${bg}' rx='14' />
        <g font-family='Inter, Arial, sans-serif' text-anchor='middle' fill='#0f172a'>
          <text x='50%' y='48%' font-size='34'>${label}</text>
          <text x='50%' y='58%' font-size='18' fill='#6b7280'>App screenshot preview</text>
        </g>
      </svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  };

  // Limit how many screenshots the carousel shows
  const MAX_SCREENSHOTS = 6;

  // Authoritative list of public screenshot images (located in public/images/screenshots)
  // Authoritative list of public screenshot images (located in public/images/screenshots)
  // Ordered per product request: 1) dashboard 2) syllabus_scanning 3) course_grid
  // 4) course_details 5) chat 6) calendar
  const SCREENSHOTS = [
    { file: 'Dashboard.png', alt: 'Dashboard – courses overview' },
    { file: 'syllabus_scanning.png', alt: 'Create new course – syllabus scanning interface' },
    { file: 'course_grid.png', alt: 'Courses grid view' },
    { file: 'course_details.png', alt: 'Course details page – course overview' },
    { file: 'chat.png', alt: 'AI chat with documents interface' },
    { file: 'calendar.png', alt: 'Event calendar view' }
  ];

  // For robustness: try both /images/screenshots/<file> and /images/<file>
  // so the carousel works whether assets were placed in screenshots/ or directly in images/.
  const screenshotCandidates = SCREENSHOTS.map(s => ({
    candidates: [
      withBase(`/images/screenshots/${s.file}`),
      withBase(`/images/${s.file}`)
    ],
    alt: s.alt
  }));

  const [screenshots, setScreenshots] = useState(
    screenshotCandidates.map(sc => ({ url: sc.candidates[0], alt: sc.alt })).slice(0, MAX_SCREENSHOTS)
  );


  // Validate which public assets actually load; for each screenshot try candidates in order
  // and keep the first successful URL. If none succeed, fall back to the inline SVG placeholder.
  useEffect(() => {
    let cancelled = false;

    const checks = screenshotCandidates.map(sc => new Promise(resolve => {
      // try each candidate sequentially
      const tryLoad = (idx) => {
        if (idx >= sc.candidates.length) return resolve({ meta: null, ok: false });
        const url = sc.candidates[idx];
        const img = new Image();
        img.onload = () => resolve({ meta: { url, alt: sc.alt }, ok: true });
        img.onerror = () => tryLoad(idx + 1);
        img.src = url;
      };
      tryLoad(0);
    }));

    Promise.all(checks).then(res => {
      if (cancelled) return;
      const ok = res.filter(r => r && r.ok).map(r => r.meta);
      if (ok.length) {
        setScreenshots(ok.slice(0, MAX_SCREENSHOTS));
      } else {
        console.warn('[LandingPage] No public screenshot images loaded. Verify files under /images/screenshots/ or /images/.');
        const placeholders = SCREENSHOTS.slice(0, 3).map((s, idx) => ({
          url: fallbackDataURI(s.alt || `Screenshot ${idx + 1}`, '#ffffff'),
          alt: s.alt || `Missing screenshot ${idx + 1}`
        }));
        setScreenshots(placeholders);
      }
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Diagnostic: preload and log each image URL to help debug why images might be 404ing
  useEffect(() => {
    if (!screenshots || !screenshots.length) return;
    screenshots.forEach(({ url }) => {
      try {
        const img = new Image();
        img.onload = () => console.info('[LandingPage] image loaded (final):', url);
        img.onerror = (e) => console.warn('[LandingPage] image FAILED to load (final):', url, e && e.message);
        img.src = url;
      } catch (err) {
        console.error('[LandingPage] error preloading image (final):', url, err);
      }
    });
  }, [screenshots]);

  // Carousel is handled by HeroCarousel component below

  // When navigation comes from another page we may receive a location.state.scrollTo
  // value (see Header.handleSectionNavigation). Also support URL hash navigation.
  // Scroll after mount (with a short delay) so the landing DOM has rendered.
  const navigate = useNavigate();
  useEffect(() => {
    const stateTarget = location.state && location.state.scrollTo;
    const hashTarget = location.hash ? location.hash.substring(1) : null;
    const target = stateTarget || hashTarget;
    if (!target) return;

    const scrollWhenReady = () => {
      const el = document.getElementById(target);
      if (el) {
        // Compute header height so the section top appears just below the header
        const header = document.querySelector('nav.landing-nav') || document.querySelector('nav');
        const headerHeight = header ? header.offsetHeight : 0;
        const top = el.getBoundingClientRect().top + window.pageYOffset - headerHeight;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

        // If we used navigation state, clear it so future navigations don't re-scroll
        if (stateTarget) {
          navigate(location.pathname, { replace: true, state: {} });
        }
        return true;
      }
      return false;
    };

    // Try immediately, otherwise poll briefly until element appears (but stop after ~1s)
    if (!scrollWhenReady()) {
      let attempts = 0;
      const interval = setInterval(() => {
        attempts += 1;
        if (scrollWhenReady() || attempts > 10) clearInterval(interval);
      }, 100);
      return () => clearInterval(interval);
    }
  }, [location, navigate]);

  // (Removed duplicate meta effect here; consolidated earlier.)

  // Founders section ref (for potential future animations)
  const foundersRef = useRef(null);

  return (
    <div className="landing-page">
      {/* Hero Section - Four Pillars */}
      <LandingHero />

      {/* Scout's Desk - Interactive AI Tools */}
      <ScoutDeskSection />

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* Founders / Mission Section (replaces trusted-by badges) */}
      <section id="founders" className="founders-section" ref={foundersRef}>
        <div className="origin-panel">
          <div className="founders-container">
            <div className="content-wrapper founders-inner" id="founders-inner">
              {/* Crest on the left */}
              <div className="crest-card" aria-hidden="true">
                <img
                  src={withBase('/other_images/bc.png')}
                  alt="Boston College crest"
                  className="crest-img"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>

              {/* Text block on the right */}
              <div className="founders-body">
                <h2 className="founders-title">Built by Students, for Students</h2>
                <div className="founders-underline" aria-hidden="true"></div>

                  <p>
                  We didn't build DocuHounds sitting in some quiet library with nothing else going on. We built it while juggling classes, work, deadlines, and everything else life throws at you.
                </p>

                <p>
                  Most tools assume you have time to plan everything perfectly. They assume you can sit down for hours, stay organized, and keep track of every little change across your classes. That's not reality.
                </p>

                <p>
                  We built DocuHounds for students who are working shifts, commuting to campus, catching up late at night, and still trying to stay on top of everything.
                </p>

                <p>
                  Upload your syllabus and your whole semester gets organized. Your assignments, deadlines, and materials all live in one place. Your study tools come from your actual coursework, not generic content.
                </p>

                <p>
                  No spreadsheets. No bouncing between five apps. No guessing what matters most today. Just a system that keeps you on track even when your schedule isn't perfect.
                </p>

                <p className="founders-subline">
                  We built the tool we wish we had when everything felt scattered. Now it's yours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section (auto-scroll only, no manual controls) */}
      <TestimonialsSection />

      {/* Pricing Section */}
      <section 
        id="pricing" 
        className="pricing pricing-locker-section"
        style={{
          position: 'relative',
          background: 'linear-gradient(to bottom, #031526 0%, #071C2C 60%, #031526 100%)',
          minHeight: '850px',
          paddingTop: '100px',
          paddingBottom: '100px',
          overflow: 'hidden'
        }}
      >
        <div className="container">
          <h2 className="section-title" style={{ color: '#ffffff', marginBottom: '3rem' }}>Choose Your Plan</h2>
          
          <div className="plan-grid-compact pricing-cards-wrapper">
            {/* Casual (Free) Tier Card */}
            <div className="plan-card-compact premium-pricing-card" data-testid="plan-free">
              <img 
                className="pricingCard__mascot" 
                src="/scout_images/Scout_Thumbsup.png" 
                alt="Scout mascot for Casual plan" 
              />
              
              <div className="premium-card-header">
                <h3 className="premium-tier-name">Casual</h3>
                <p className="premium-identity-line">For staying organized without friction</p>
              </div>
              
              <div className="premium-price-section">
                <div className="premium-price">$0<span className="premium-price-period"> / month</span></div>
              </div>
              
              <div className="premium-divider"></div>
              
              <ul className="premium-features-list">
                <li><span className="premium-check">✓</span>Syllabus upload with automatic calendar syncing and deadline timeline</li>
                <li><span className="premium-check">✓</span>Assignments and class events automatically organized by course</li>
                <li><span className="premium-check">✓</span>Course materials stored and grouped by class</li>
                <li><span className="premium-check">✓</span>Central dashboard to view classes, deadlines, and progress</li>
              </ul>
              
              <p className="premium-best-for">Students who want structure without overthinking it.</p>
            </div>

            {/* Locked-In (Basic) Plan Card */}
            <div className="plan-card-compact premium-pricing-card" data-testid="plan-basic">
              <img 
                className="pricingCard__mascot" 
                src="/scout_images/Scout_Mid.png" 
                alt="Scout mascot for Locked-In plan" 
              />
              
              <div className="premium-card-header">
                <h3 className="premium-tier-name">Locked-In</h3>
                <p className="premium-identity-line">For students who don't miss deadlines</p>
              </div>
              
              <div className="premium-price-section">
                <div className="premium-price">$4.99<span className="premium-price-period"> / month</span></div>
              </div>
              
              <div className="premium-divider"></div>
              
              <ul className="premium-features-list">
                <li><span className="premium-check">✓</span>All organization and planning features from the Casual plan</li>
                <li><span className="premium-check">✓</span>AI-generated quizzes from your course materials</li>
                <li><span className="premium-check">✓</span>AI-generated flashcards from your course materials</li>
                <li><span className="premium-check">✓</span>Fast review and study tools built from what you upload</li>
              </ul>
              
              <p className="premium-best-for">Students who are serious about grades and consistency.</p>
            </div>

            {/* All-Gas (Pro) Plan Card */}
            <div className="plan-card-compact premium-pricing-card premium-card-featured" data-testid="plan-pro">
              <div className="premium-most-popular-badge">
                Most Popular
              </div>
              
              <img 
                className="pricingCard__mascot" 
                src="/scout_images/Scout_Extreme.png" 
                alt="Scout mascot for All-Gas plan" 
              />
              
              <div className="premium-card-header">
                <h3 className="premium-tier-name">All-Gas</h3>
                <p className="premium-identity-line">For total immersion and zero excuses</p>
              </div>
              
              <div className="premium-price-section">
                <div className="premium-price">$11.99<span className="premium-price-period"> / month</span></div>
              </div>
              
              <div className="premium-divider"></div>
              
              <ul className="premium-features-list">
                <li><span className="premium-check">✓</span>All planning and AI study tools from the Locked-In plan</li>
                <li><span className="premium-check">✓</span>Audiobooks generated from your course materials</li>
                <li><span className="premium-check">✓</span>AI-generated study podcasts from your classes</li>
                <li><span className="premium-check">✓</span>Learn hands-free while commuting, training, or walking</li>
              </ul>
              
              <p className="premium-best-for">Students who want every possible edge.</p>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <div style={{ 
              fontSize: '0.8125rem', 
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.5)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: '0.5rem'
            }}>
              Beta Pricing
            </div>
            <p style={{ 
              fontSize: '0.875rem', 
              color: 'rgba(255, 255, 255, 0.4)',
              maxWidth: '520px',
              margin: '0 auto',
              lineHeight: '1.5'
            }}>
              Early access pricing while DocuHounds is in beta. Features may expand over time.
            </p>
          </div>
        </div>
      </section>

      {/* Blog preview removed per request - blog is now accessible via footer link only */}

      {/* Video Modal */}
      {showVideoModal && (
        <div className="video-modal" onClick={() => setShowVideoModal(false)}>
          <div className="video-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close"
              onClick={() => setShowVideoModal(false)}
              aria-label="Close video modal"
            >
              ×
            </button>
            <div className="video-placeholder">
              <div className="video-icon">📹</div>
              <h3>Demo Video Coming Soon!</h3>
              <p>We're putting the finishing touches on our demo video. Check back soon to see DocuHounds in action!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LandingPage;